import { JapanArea } from "@/types/generated";
import { IHotelListSliderProps, THotelData } from "../hotel-list-slider";
import { SearchAvailableRoom } from "../search-avaliable/SearchAvailableRoomMobile";
import { TYPE_GROUP } from "@/utils";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";

interface HandleFilterByFilterDataProps {
  dataFilter: SearchAvailableRoom | null;
  setFilteredSections: React.Dispatch<
    React.SetStateAction<IHotelListSliderProps[]>
  >;
  hotelSliderSections: IHotelListSliderProps[];
  japanAreas: JapanArea[];
  currentBrand?: HotelBrandItemNullable;
  setVisibleHotels: React.Dispatch<React.SetStateAction<THotelData[]>>;
  currentCity?: string;
  isFirstCityChange?: React.MutableRefObject<boolean>;
}

// Helper: Extract unique hotels from sections
const uniqHotels = (sections: IHotelListSliderProps[]): THotelData[] =>
  Array.from(
    sections
      .flatMap((s) => s.data)
      .reduce(
        (m, h) => (h.documentId ? m.set(h.documentId, h) : m),
        new Map<string, THotelData>(),
      )
      .values(),
  );

// Helper: Check if any filters are applied
const _hasFilters = (
  dataFilter: SearchAvailableRoom | null,
  currentCity?: string,
  currentBrand?: HotelBrandItemNullable,
): boolean => {
  return !!(
    dataFilter?.hotelOrCity ||
    dataFilter?.brands?.length ||
    dataFilter?.preferences?.length ||
    currentCity ||
    currentBrand
  );
};

// Helper: Check if filtering by current city with no other filters
const isCurrentCityOnlyFilter = (
  dataFilter: SearchAvailableRoom | null,
  currentCity: string,
  hotelSliderSections: IHotelListSliderProps[],
): boolean => {
  return !!(
    dataFilter?.hotelOrCity?.type === "city" &&
    dataFilter?.hotelOrCity?.value?.toLowerCase() ===
      currentCity.toLowerCase() &&
    !dataFilter?.brands?.length &&
    !dataFilter?.preferences?.length &&
    hotelSliderSections.some(
      (section) =>
        section.locationId?.toLowerCase() === currentCity.toLowerCase(),
    )
  );
};

// Helper: Get hotel IDs from area
const getHotelIdsFromArea = (area: JapanArea | undefined): string[] => {
  return (
    area?.hotels
      ?.map((h) => h?.documentId)
      .filter((id): id is string => typeof id === "string") ?? []
  );
};

// Helper: Filter sections by hotel IDs in area
const filterSectionsByHotelIds = (
  sections: IHotelListSliderProps[],
  hotelIds: string[],
): IHotelListSliderProps[] => {
  return sections
    .map((section) => {
      if (section.typeGroup === TYPE_GROUP.BASIC) {
        const data = section.data.filter(
          (h) => h?.documentId && hotelIds.includes(h.documentId),
        );
        return data?.length ? { ...section, data } : null;
      }
      return section;
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);
};

// Helper: Filter sections by brands
const filterSectionsByBrands = (
  sections: IHotelListSliderProps[],
  brandValues: string[],
): IHotelListSliderProps[] => {
  const brandSet = new Set(brandValues);
  return sections
    .map((sec) => {
      if (sec.typeGroup === TYPE_GROUP.BASIC) {
        const filteredData = sec.data.filter((h) => brandSet.has(h.brand));
        return filteredData?.length ? { ...sec, data: filteredData } : null;
      }
      return sec;
    })
    .filter((sec): sec is NonNullable<typeof sec> => !!sec);
};

// Helper: Filter sections by preferences (benefits)
const filterSectionsByPreferences = (
  sections: IHotelListSliderProps[],
  preferenceValues: string[],
): IHotelListSliderProps[] => {
  const prefSet = new Set(preferenceValues);
  return sections
    .map((sec) => {
      if (sec.typeGroup === TYPE_GROUP.BASIC) {
        const filteredData = sec.data.filter((h) => {
          const benefitSet = new Set(h.benefits ?? []);
          return Array.from(prefSet).some((p) => benefitSet.has(p));
        });
        return filteredData?.length ? { ...sec, data: filteredData } : null;
      }
      return sec;
    })
    .filter((sec): sec is NonNullable<typeof sec> => !!sec);
};

// Helper: Apply all active filters to sections
const applyFiltersToSections = (
  sections: IHotelListSliderProps[],
  dataFilter: SearchAvailableRoom | null,
): IHotelListSliderProps[] => {
  let filteredSections = sections;

  if (dataFilter?.brands?.length) {
    filteredSections = filterSectionsByBrands(
      filteredSections,
      dataFilter.brands.map((b) => b.value),
    );
  }

  if (dataFilter?.preferences?.length) {
    filteredSections = filterSectionsByPreferences(
      filteredSections,
      dataFilter.preferences.map((p) => p.value),
    );
  }

  return filteredSections;
};

// Helper: Handle city filter
const handleCityFilter = (
  dataFilter: SearchAvailableRoom,
  japanAreas: JapanArea[],
  hotelSliderSections: IHotelListSliderProps[],
): IHotelListSliderProps[] | null => {
  const currentArea = japanAreas.find(
    (area) => area?.documentId === dataFilter?.hotelOrCity?.value,
  );

  if (!currentArea) {
    return null;
  }

  const hotelIds = getHotelIdsFromArea(currentArea);
  const sections = filterSectionsByHotelIds(hotelSliderSections, hotelIds);

  return applyFiltersToSections(sections, dataFilter);
};

// Helper: Handle hotel filter
const handleHotelFilter = (
  dataFilter: SearchAvailableRoom,
  hotelSliderSections: IHotelListSliderProps[],
): IHotelListSliderProps[] => {
  const hotelIdToFilter = dataFilter.hotelOrCity?.value;
  const sections = hotelSliderSections
    .map((section) => {
      if (section.typeGroup === TYPE_GROUP.BASIC) {
        const data = section.data.filter(
          (h) => h?.documentId === hotelIdToFilter,
        );
        return data?.length ? { ...section, data } : null;
      }
      return section;
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);

  return applyFiltersToSections(sections, dataFilter);
};

// Helper: Handle brand and preference filters without hotel/city
const handleBrandAndPreferenceFilters = (
  hotelSliderSections: IHotelListSliderProps[],
  dataFilter: SearchAvailableRoom | null,
): IHotelListSliderProps[] => {
  return applyFiltersToSections(hotelSliderSections, dataFilter);
};

export const handleFilterByFilterData = ({
  dataFilter,
  japanAreas,
  hotelSliderSections,
  setFilteredSections,
  currentBrand,
  setVisibleHotels,
  currentCity,
}: HandleFilterByFilterDataProps) => {
  // No filters applied - return all sections
  if (
    !dataFilter?.hotelOrCity &&
    !dataFilter?.brands?.length &&
    !dataFilter?.preferences?.length &&
    !currentCity &&
    !currentBrand
  ) {
    setFilteredSections(hotelSliderSections);
    setVisibleHotels(uniqHotels(hotelSliderSections));
    return;
  }

  // Current city only filter - return all sections
  if (
    currentCity &&
    isCurrentCityOnlyFilter(dataFilter, currentCity, hotelSliderSections)
  ) {
    setFilteredSections(hotelSliderSections);
    setVisibleHotels(uniqHotels(hotelSliderSections));
    return;
  }

  // City filter
  if (dataFilter?.hotelOrCity?.type === "city") {
    const sections = handleCityFilter(
      dataFilter,
      japanAreas,
      hotelSliderSections,
    );
    if (!sections) {
      setFilteredSections([]);
      setVisibleHotels([]);
      return;
    }
    setVisibleHotels(
      uniqHotels(sections.filter((s) => s.typeGroup === TYPE_GROUP.BASIC)),
    );
    setFilteredSections(sections);
    return;
  }

  // Hotel filter
  if (dataFilter?.hotelOrCity?.type === "hotel") {
    const sections = handleHotelFilter(dataFilter, hotelSliderSections);
    setVisibleHotels(
      uniqHotels(sections.filter((s) => s.typeGroup === TYPE_GROUP.BASIC)),
    );
    setFilteredSections(sections);
    return;
  }

  // Brand and/or preference filters only
  if (
    !dataFilter?.hotelOrCity &&
    (dataFilter?.brands?.length || dataFilter?.preferences?.length)
  ) {
    const sections = handleBrandAndPreferenceFilters(
      hotelSliderSections,
      dataFilter,
    );
    setVisibleHotels(
      uniqHotels(sections.filter((s) => s.typeGroup === TYPE_GROUP.BASIC)),
    );
    setFilteredSections(sections);
    return;
  }

  // Fallback: return all sections
  setFilteredSections(hotelSliderSections);
  setVisibleHotels(uniqHotels(hotelSliderSections));
};
